package org.example.project.enginefinanzen

import androidx.compose.runtime.*
import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.launch
import kotlinx.serialization.Serializable
import kotlinx.serialization.builtins.ListSerializer
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import org.example.project.dashboard.AppDialogs
import org.example.project.enginetabular.TABULAR_DOCUMENT_ICON_VALUE
import org.example.project.enginetabular.TabularScreen
import org.example.project.enginetabular.TabularSplitScreen
import org.example.project.manager.LumaticaApiManager
import org.example.project.manager.LumaticaApiSettings
import org.example.project.screens_sys.EingangsbelegeApi
import org.example.project.service.ServiceDocumentViewerService
import org.example.project.utils.All
import org.jetbrains.compose.web.css.height
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.dom.Div
import org.w3c.fetch.RequestInit

private val finanzamtHeaders = listOf("Jahr", "Belege", "Positionen", "Brutto", "MwSt.", "Netto")
private val quartalHeaders = listOf("Quartal", "Belege", "Positionen", "Brutto", "MwSt.", "Netto", "Antrag erstellt", "Ausgeglichen", "PDF")
private const val checked = "\u2705"
private const val unchecked = "\u2B1C"

@Serializable
private data class QuartalStatus(
    val id: String,
    val antragErstellt: Boolean = false,
    val ausgeglichen: Boolean = false,
    val dokumentName: String = ""
)

private object FinanzamtQuartaleApi {
    private val json = Json { ignoreUnknownKeys = true }

    @Serializable
    private data class ApiError(val ok: Boolean = true, val error: String = "")

    private fun requireSuccess(body: String): String {
        val failure = runCatching { json.decodeFromString<ApiError>(body) }.getOrNull()
        if (failure?.ok == false) {
            throw IllegalStateException(when (failure.error) {
                "finanzamt_berechtigung_fehlt", "finanzdashboard_berechtigung_fehlt", "schreibrecht_fehlt" ->
                    "Keine Berechtigung für Finanzamt-Quartale."
                else -> failure.error.ifBlank { "Finanzamt-Daten konnten nicht geladen werden." }
            })
        }
        return body
    }

    suspend fun load(): List<QuartalStatus> = json.decodeFromString(
        ListSerializer(QuartalStatus.serializer()),
        requireSuccess(LumaticaApiManager.call("finanzdashboard/quartalsstatus"))
    )

    suspend fun save(status: QuartalStatus): QuartalStatus = json.decodeFromString(
        QuartalStatus.serializer(),
        requireSuccess(LumaticaApiManager.postJson("finanzdashboard/quartalsstatus-save", json.encodeToString(status)))
    )

    suspend fun openPdf(status: QuartalStatus): Boolean {
        if (status.dokumentName.isBlank()) return false
        val response = window.fetch(
            "${LumaticaApiSettings.getBaseUrl()}/api/finanzdashboard/quartalsdokument/${status.id}",
            RequestInit(method = "GET", headers = LumaticaApiManager.auditHeaders())
        ).await()
        if (!response.ok) return false
        val blob = response.blob().await()
        val url = window.asDynamic().URL.createObjectURL(blob) as String
        ServiceDocumentViewerService.show(status.dokumentName, url)
        return true
    }
}

private fun moneyCents(value: String): Long {
    val normalized = value.trim().replace("€", "").replace(" ", "")
        .let { if (',' in it) it.replace(".", "").replace(',', '.') else it }
    return kotlin.math.round((normalized.toDoubleOrNull() ?: 0.0) * 100).toLong()
}

private fun money(value: Long): String =
    "${if (value < 0) "-" else ""}${kotlin.math.abs(value) / 100}.${(kotlin.math.abs(value) % 100).toString().padStart(2, '0')}"

private fun summaryRows(rows: List<All>, label: String): List<String> = listOf(
    label,
    rows.sumOf { it.belegAnzahl }.toString(),
    rows.sumOf { it.positionAnzahl }.toString(),
    money(rows.sumOf { moneyCents(it.brutto) }),
    money(rows.sumOf { moneyCents(it.mwst) }),
    money(rows.sumOf { moneyCents(it.netto) })
)

@Composable
fun SysFinanzamtQuartaleScreen() {
    val scope = rememberCoroutineScope()
    var monate by remember { mutableStateOf<List<All>>(emptyList()) }
    var statuses by remember { mutableStateOf<Map<String, QuartalStatus>>(emptyMap()) }
    var selectedYear by remember { mutableStateOf<String?>(null) }
    var tableVersion by remember { mutableStateOf(0) }

    suspend fun reload() {
        try {
            monate = EingangsbelegeApi.loadMonate()
            statuses = FinanzamtQuartaleApi.load().associateBy { it.id }
            val years = (monate.map { it.monat.ifBlank { it.id }.take(4) } + statuses.keys.map { it.take(4) })
                .filter { it.matches(Regex("\\d{4}")) }.distinct()
            selectedYear = selectedYear?.takeIf { it in years } ?: years.maxOrNull()
            tableVersion++
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            AppDialogs.alert(e.message ?: "Finanzamtdaten konnten nicht geladen werden.")
        }
    }

    LaunchedEffect(Unit) { reload() }

    val years = (monate.map { it.monat.ifBlank { it.id }.take(4) } + statuses.keys.map { it.take(4) })
        .filter { it.matches(Regex("\\d{4}")) }.distinct().sortedDescending()
    val yearData = years.map { year ->
        year to summaryRows(monate.filter { it.monat.ifBlank { it.id }.startsWith("$year-") }, year)
    }
    val quarterData = selectedYear?.let { year ->
        (1..4).map { quarter ->
            val id = "$year-Q$quarter"
            val rows = monate.filter { row ->
                val month = row.monat.ifBlank { row.id }.substringAfter("-", "").take(2).toIntOrNull()
                row.monat.ifBlank { row.id }.startsWith("$year-") && month != null && month in ((quarter - 1) * 3 + 1)..(quarter * 3)
            }
            val status = statuses[id] ?: QuartalStatus(id)
            id to (summaryRows(rows, "Q$quarter $year") + listOf(
                if (status.antragErstellt) checked else unchecked,
                if (status.ausgeglichen) checked else unchecked,
                if (status.dokumentName.isNotBlank()) TABULAR_DOCUMENT_ICON_VALUE else ""
            ))
        }
    }.orEmpty()

    Div({ style { height(100.percent) } }) {
        TabularSplitScreen(
            initialRatio = 0.45f,
            screen1 = {
                TabularScreen(
                    screenKey = "FinanzamtQuartaleJahre",
                    rollenKey = "FinanzamtQuartale",
                    title = "Finanzamt – Jahre",
                    headers = finanzamtHeaders,
                    data = yearData,
                    editable = false,
                    includeMetadata = false,
                    onCommit = {},
                    onRowClick = { selectedYear = it },
                    onSelectedRowsChange = { rows -> rows.singleOrNull()?.let { selectedYear = it } },
                    onRefresh = { scope.launch { reload() } }
                )
            },
            screen2 = {
                key(tableVersion, selectedYear) {
                    TabularScreen(
                        screenKey = "FinanzamtQuartaleDetails",
                        entityScope = selectedYear.orEmpty(),
                        rollenKey = "FinanzamtQuartale",
                        title = selectedYear?.let { "Finanzamt – Quartale $it" } ?: "Finanzamt – Quartale",
                        headers = quartalHeaders,
                        data = quarterData,
                        editable = true,
                        commitChangedRowsOnly = true,
                        includeMetadata = false,
                        toggleColumns = setOf("Antrag erstellt", "Ausgeglichen"),
                        nonEditableCols = quartalHeaders.filterNot { it == "Ausgeglichen" }.toSet(),
                        allowCreate = false,
                        allowDelete = false,
                        allowImportButton = false,
                        onCommit = {},
                        onCommitConfirmed = { changed, complete ->
                            scope.launch {
                                try {
                                    val saved = changed.map { (id, values) ->
                                        FinanzamtQuartaleApi.save(QuartalStatus(
                                            id,
                                            antragErstellt = values.getOrNull(6) == checked,
                                            ausgeglichen = values.getOrNull(7) == checked
                                        ))
                                    }
                                    statuses = statuses + saved.associateBy { it.id }
                                    complete(true)
                                } catch (e: CancellationException) {
                                    complete(false)
                                    throw e
                                } catch (e: Exception) {
                                    AppDialogs.alert(e.message ?: "Quartalsstatus konnte nicht gespeichert werden.")
                                    complete(false)
                                }
                            }
                        },
                        onCellClick = { id, column ->
                            val status = statuses[id] ?: QuartalStatus(id)
                            if (column == 8 && status.dokumentName.isNotBlank()) {
                                scope.launch {
                                    try {
                                        if (!FinanzamtQuartaleApi.openPdf(status)) AppDialogs.alert("PDF konnte nicht geöffnet werden.")
                                    } catch (e: CancellationException) {
                                        throw e
                                    } catch (e: Exception) {
                                        AppDialogs.alert(e.message ?: "PDF konnte nicht geöffnet werden.")
                                    }
                                }
                            }
                        },
                        onRefresh = { scope.launch { reload() } }
                    )
                }
            }
        )
    }
}
